'use client';

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Check, X } from '../icons';
import styles from './tick.module.css';

export interface TickProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Ticked. Rendered as `aria-pressed`: a tick is a toggle, and the same
   *  line un-ticks. */
  pressed: boolean;
  /** Names the act for screen readers ("Done: <title>" / "Undo: <title>").
   *  With children the row's visible content stays the children; without,
   *  the label is the button's whole name. */
  label: string;
  /**
   * The mark inside the box once pressed. `check` is the finished line;
   * `cross` is the CANCELLED reading - closed with nothing delivered
   * (docs/CLOSED_AXIS.md). A different GLYPH, never a different colour: the
   * two readings must be told apart, and a cancelled line at rest is not a
   * thing that needs attention.
   */
  mark?: 'check' | 'cross';
  /** The row's content, seated beside the box, when the whole row is the
   *  checkbox (the conclusion question's rows). Absent, the tick is the box
   *  alone (the checklist rail's lines). */
  children?: ReactNode;
}

/**
 * The tick: the one place a checkbox is drawn - a hairline rounded square,
 * the check inside once pressed, or a ✕ where the line was CANCELLED rather
 * than finished (`mark`, 2026-09-20). It is a real button, never an input: it
 * fires an act through the spine and holds no form state. The Work list's
 * rows carry no tick at all (the list is a view; its glyph stays the solid
 * health mark, MACHINE.md §1).
 *
 * Two shapes (2026-09-27):
 *
 * - **The box alone** (no children): the checklist rail's lines
 *   (docs/CHECKLIST.md §3.1, "body opens, box ticks", Leon 2026-09-27). Only
 *   the box ticks; the host seats the way into the item BESIDE it, never
 *   inside (a link in a button is invalid). The button is a `--tick-size`
 *   square around the box - 24px on a mouse, 44px on a coarse pointer, the
 *   touch floor of MOBILE.md §3 - and a host that lays its row out around the
 *   18px box cancels that reach with a negative margin.
 * - **The whole row** (children): the conclusion question's sub-tasks
 *   (`src/app/items/[itemId]/conclusion-question.tsx`), where the row IS the
 *   choice and nothing on it leads anywhere else. The box sits at the row's
 *   leading edge, the content beside it.
 */
export const Tick = forwardRef<HTMLButtonElement, TickProps>(function Tick(
  { pressed, label, mark = 'check', children, className, type, ...rest },
  ref,
) {
  const bare = children === undefined;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={[styles.tick, bare && styles.bare, className].filter(Boolean).join(' ')}
      aria-pressed={pressed}
      aria-label={label}
      {...rest}
    >
      {/* One inner flex row, not flex on the button itself: WebKit lays a
          button's children out in its own centered anonymous box and does
          not honour `display: flex` on the button reliably (the box landed
          mid-title on an iPhone, 2026-09-07). */}
      <span className={styles.inner}>
        <span className={styles.box} data-mark={mark} aria-hidden>
          {mark === 'cross' ? <X /> : <Check />}
        </span>
        {!bare && <span className={styles.content}>{children}</span>}
      </span>
    </button>
  );
});
